// Saved commission and fee rates for one account: typed in once, then added to every trade
// in that account that arrives without fees (see feeMath.js and the server's applyFeeRules).
import { useMemo, useState } from 'react';
import { api } from './api';
import { usd } from './pnl';
import { useI18n } from './i18n';
import { CONTRACTS, rootSymbol } from './tools/contracts';
import { feeFor, roundTripPerContract, tradesToUpdate } from './feeMath';

const blankRow = (symbol) => ({ symbol, commission: '', fees: '' });

function rowsFrom(rule) {
  if (!rule || !rule.rates || !rule.rates.length) return [blankRow('ALL')];
  const rows = rule.rates.map((r) => ({ symbol: r.symbol, commission: r.commission ? String(r.commission) : '', fees: r.fees ? String(r.fees) : '' }));
  // The "All contracts" row always comes first.
  return rows.some((r) => r.symbol === 'ALL') ? [rows.find((r) => r.symbol === 'ALL'), ...rows.filter((r) => r.symbol !== 'ALL')] : [blankRow('ALL'), ...rows];
}

const num = (v) => (v === '' ? 0 : Number(v));

// One line describing saved rates, e.g. "$0.50 commission + $0.87 fees per contract per side · MNQ $0.25 + $0.37".
export function FeeRuleSummary({ rule }) {
  const { t } = useI18n();
  if (!rule || !rule.rates || !rule.rates.length) return null;
  const money = (n) => usd.format(n || 0);
  const all = rule.rates.find((r) => r.symbol === 'ALL');
  const others = rule.rates.filter((r) => r.symbol !== 'ALL');
  const per = rule.per === 'side' ? t('per contract, per side') : t('per contract, per round trip');
  return (
    <span className="fee-rule-summary">
      {all && (all.commission || all.fees)
        ? t('{commission} commission + {fees} fees {per}', { commission: money(all.commission), fees: money(all.fees), per })
        : per}
      {others.map((r) => <span key={r.symbol}> &middot; {r.symbol} {money(r.commission)} + {money(r.fees)}</span>)}
    </span>
  );
}

export default function FeeRuleForm({ account, trades, onSaved, onCancel, onSessionExpired }) {
  const { t } = useI18n();
  const [per, setPer] = useState(account.feeRule ? account.feeRule.per : 'side');
  const [rows, setRows] = useState(() => rowsFrom(account.feeRule));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const valid = rows.every((r) => [r.commission, r.fees].every((v) => v === '' || (Number(v) >= 0 && Number(v) <= 1000)))
    && rows.some((r) => num(r.commission) + num(r.fees) > 0);
  const rule = { per, rates: rows.map((r) => ({ symbol: r.symbol, commission: num(r.commission), fees: num(r.fees) })) };
  const affected = valid ? tradesToUpdate(rule, trades) : [];
  const added = affected.reduce((s, x) => s + feeFor(rule, x) - (x.fees || 0), 0);

  // Contracts that can get their own rate: the ones traded in this account first, then the usual list.
  const choices = useMemo(() => {
    const traded = [...new Set(trades.map((x) => rootSymbol(x.symbol)))].filter((s) => /^[A-Z0-9]{1,6}$/.test(s));
    return [...new Set([...traded, ...CONTRACTS.map((c) => c.symbol)])];
  }, [trades]);
  const unused = choices.filter((s) => !rows.some((r) => r.symbol === s));

  const setRow = (i, changes) => setRows((list) => list.map((r, j) => (j === i ? { ...r, ...changes } : r)));

  async function save(e) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await api(`/accounts/${account._id}/fees`, { method: 'PUT', body: rule });
      onSaved(result);
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      else setError(err.message);
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError('');
    try {
      const updated = await api(`/accounts/${account._id}/fees`, { method: 'DELETE' });
      onSaved({ account: updated, trades: [] });
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      else setError(err.message);
      setBusy(false);
    }
  }

  return (
    <form className="fee-rule-form" onSubmit={save}>
      <p className="fee-rule-intro">
        {t('Saved for {name}: added to every trade in this account that comes in without fees, now and on future imports, until you change them.', { name: account.name })}
      </p>
      <div className="impact-buttons tracking-toggle fee-rule-per" role="radiogroup" aria-label={t('Rates are')}>
        {[['side', t('Per contract, per side')], ['round', t('Per contract, round trip')]].map(([id, label]) => (
          <button key={id} type="button" role="radio" aria-checked={per === id} className={`secondary small${per === id ? ' active' : ''}`} onClick={() => setPer(id)}>
            {label}
          </button>
        ))}
      </div>
      <div className="fee-rule-rows">
        {rows.map((r, i) => {
          const perContract = roundTripPerContract({ per }, { commission: num(r.commission), fees: num(r.fees) });
          return (
            <div key={r.symbol} className="fee-rule-row">
              <span className="fee-rule-symbol">{r.symbol === 'ALL' ? t('All contracts') : r.symbol}</span>
              <label>
                <span>{t('Commission')}</span>
                <span className="commission-input"><span>$</span>
                  <input type="number" step="0.01" min="0" max="1000" value={r.commission} placeholder="0.00"
                    onChange={(e) => setRow(i, { commission: e.target.value })} aria-label={t('Commission for {symbol}', { symbol: r.symbol === 'ALL' ? t('All contracts') : r.symbol })} />
                </span>
              </label>
              <label>
                <span>{t('Fees')}</span>
                <span className="commission-input"><span>$</span>
                  <input type="number" step="0.01" min="0" max="1000" value={r.fees} placeholder="0.00"
                    onChange={(e) => setRow(i, { fees: e.target.value })} aria-label={t('Fees for {symbol}', { symbol: r.symbol === 'ALL' ? t('All contracts') : r.symbol })} />
                </span>
              </label>
              <span className="fee-rule-total">{perContract > 0 ? t('{amount} per contract in and out', { amount: usd.format(perContract) }) : ''}</span>
              {r.symbol !== 'ALL' && (
                <button type="button" className="link-button" onClick={() => setRows((list) => list.filter((_, j) => j !== i))} aria-label={t('Remove {symbol}', { symbol: r.symbol })}>&times;</button>
              )}
            </div>
          );
        })}
      </div>
      {unused.length > 0 && (
        <label className="fee-rule-add">
          <span>{t('Different rate for one contract?')}</span>
          <select value="" onChange={(e) => e.target.value && setRows((list) => [...list, blankRow(e.target.value)])}>
            <option value="">{t('Add a contract…')}</option>
            {unused.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      )}
      <span className="field-hint">
        {!valid
          ? t('Enter the commission, the fees or both, as listed in your platform’s fee schedule or cash history.')
          : affected.length
            ? t('Saving adds {total} in fees to {n} trades in this account. Fees from your file or typed in yourself aren’t changed.', { total: usd.format(added), n: affected.length })
            : t('No trades need fees right now. New trades without fees will get them automatically.')}
      </span>
      <div className="missing-fees-actions">
        <button type="submit" className="small" disabled={!valid || busy}>{busy ? t('Saving…') : t('Save rates')}</button>
        <button type="button" className="secondary small" onClick={onCancel} disabled={busy}>{t('Cancel')}</button>
        {account.feeRule && (
          <button type="button" className="link-button danger-link" onClick={remove} disabled={busy}>{t('Stop adding fees automatically')}</button>
        )}
      </div>
      {error && <p className="error">{error}</p>}
    </form>
  );
}
